import DOMPurify from "dompurify";

/** Tags that behave reasonably across common email clients for simple marketing copy. */
const EMAIL_ALLOWED_TAGS = ["p", "br", "strong", "b", "em", "i", "a", "ul", "ol", "li", "span", "img"];

const EMAIL_ALLOWED_ATTR = ["href", "target", "rel", "src", "alt", "width", "height", "title", "style"];

/** Only http(s) image URLs — matches typical hosted assets for email. */
const EMAIL_IMG_SRC_SAFE = /^https?:\/\//i;

/** TipTap / email: paragraph text-align only (no arbitrary CSS). */
const PARAGRAPH_TEXT_ALIGN_STYLE = /^text-align:\s*(left|right|center|justify)\s*(;\s*)?$/i;

const EMAIL_MAX_IMG_SIZE_PX = 600;

function normalizePositivePxDimension(rawValue: string | null): string | null {
  if (!rawValue) return null;
  const normalized = rawValue.trim();
  if (!/^\d+$/.test(normalized)) return null;
  const asNumber = Number(normalized);
  if (!Number.isFinite(asNumber) || asNumber <= 0) return null;
  return String(Math.min(Math.floor(asNumber), EMAIL_MAX_IMG_SIZE_PX));
}

let domPurifyStyleHookInstalled = false;

function ensureEmailStyleAttributeHook(): void {
  if (domPurifyStyleHookInstalled) return;
  domPurifyStyleHookInstalled = true;
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName !== "style") return;
    const tag = (node as Element).tagName;
    if (tag === "P") {
      const v = data.attrValue.replace(/\s+/g, " ").trim();
      if (!PARAGRAPH_TEXT_ALIGN_STYLE.test(v)) {
        data.keepAttr = false;
      }
      return;
    }
    if (tag === "IMG") {
      data.keepAttr = false;
      return;
    }
    data.keepAttr = false;
  });
}

/**
 * Sanitizes HTML for notification email bodies: safe subset for storage and sending.
 */
export function sanitizeEmailHtml(html: string): string {
  ensureEmailStyleAttributeHook();
  const dirty = html ?? "";
  const clean = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: EMAIL_ALLOWED_TAGS,
    ALLOWED_ATTR: EMAIL_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });

  const doc = new DOMParser().parseFromString(clean, "text/html");
  doc.querySelectorAll("img").forEach((el) => {
    const src = el.getAttribute("src")?.trim() ?? "";
    if (!src || !EMAIL_IMG_SRC_SAFE.test(src)) {
      el.remove();
      return;
    }
    const width = normalizePositivePxDimension(el.getAttribute("width"));
    const height = normalizePositivePxDimension(el.getAttribute("height"));

    if (width) {
      el.setAttribute("width", width);
    } else {
      el.removeAttribute("width");
    }

    if (height) {
      el.setAttribute("height", height);
    } else {
      el.removeAttribute("height");
    }

    const widthStyle = width ? `;width:${width}px` : "";
    const heightStyle = height ? `;height:${height}px` : "";
    el.setAttribute(
      "style",
      `max-width:${EMAIL_MAX_IMG_SIZE_PX}px;max-height:${EMAIL_MAX_IMG_SIZE_PX}px;display:block;margin:12px auto${widthStyle}${heightStyle}`
    );
  });
  doc.querySelectorAll("p").forEach((el) => {
    if (el.innerHTML.trim() === "") {
      el.innerHTML = "<br>";
    }
  });
  return doc.body.innerHTML.trim();
}
